// chatApi.js
import { request } from "./http";
import { buildReactionTogglePayload, REACTION_TARGET_TYPES } from "../ui/reactions/reactionModel";

function pickArray(res) {
    if (Array.isArray(res)) return res;
    return res?.items || res?.content || res?.data || [];
}

function canFallbackEndpoint(err) {
    const st = err?.status ?? err?.response?.status ?? null;
    return st === 404 || st === 405 || st === 501;
}

async function requestWithEndpointFallback(primaryPath, fallbackPath, options = {}) {
    try {
        return await request(primaryPath, options);
    } catch (err) {
        if (!fallbackPath || !canFallbackEndpoint(err)) throw err;
        return request(fallbackPath, options);
    }
}

async function requestFirstAvailable(paths, options = {}) {
    let lastErr = null;
    for (const path of paths.filter(Boolean)) {
        try {
            return await request(path, options);
        } catch (err) {
            lastErr = err;
            if (!canFallbackEndpoint(err)) throw err;
        }
    }
    throw lastErr;
}

function unwrapPayload(raw) {
    if (raw && typeof raw === "object") {
        if (raw.item && typeof raw.item === "object") return raw.item;
        if (raw.items && typeof raw.items === "object") return raw.items;
    }
    return raw;
}

function parsePositiveInt(value, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return Math.max(1, Math.floor(n));
}

function normalizeMessagesCursor(cursor) {
    if (cursor == null || cursor === "") return { cursorValue: null, page: null };

    if (typeof cursor === "object") {
        const cursorRaw =
            cursor.cursor ??
            cursor.nextCursor ??
            cursor.next ??
            cursor.before ??
            cursor.beforeMessageId ??
            cursor.messageId ??
            null;
        const cursorValue = cursorRaw == null ? null : String(cursorRaw).trim() || null;

        const pageRaw = cursor.page ?? cursor.pageIndex ?? cursor.number ?? null;
        const pageNum = Number(pageRaw);
        const page = Number.isFinite(pageNum) && pageNum >= 0 ? Math.floor(pageNum) : null;

        return { cursorValue, page };
    }

    const cursorValue = String(cursor).trim();
    return { cursorValue: cursorValue || null, page: null };
}

function extractMessageId(value) {
    if (!value || typeof value !== "object") return null;
    const id = value.id ?? value.messageId ?? value.msgId ?? value.message_id ?? null;
    if (id == null || id === "") return null;
    return String(id);
}

function pickNextMessagesCursor(raw, items, pageSize, normalizedCursor) {
    const res = raw && typeof raw === "object" ? raw : null;
    if (!res) return null;

    const directCursor =
        res.nextCursor ??
        res.next_cursor ??
        res.cursorNext ??
        res.next_page_cursor ??
        null;
    if (directCursor != null && String(directCursor).trim() !== "") return directCursor;

    const hasMoreFlag = res.hasMore ?? res.has_more ?? res.hasNext ?? res.has_next ?? null;
    if (hasMoreFlag === false) return null;

    const number = Number(res.number ?? res.page?.number ?? res.pageable?.number ?? NaN);
    const totalPages = Number(res.totalPages ?? res.page?.totalPages ?? res.pageable?.totalPages ?? NaN);
    if (Number.isFinite(number) && Number.isFinite(totalPages) && totalPages > number + 1) {
        return { page: number + 1 };
    }

    const last = res.last ?? res.page?.last ?? res.pageable?.last ?? null;
    if (last === false && Number.isFinite(number)) {
        return { page: number + 1 };
    }

    const oldestItem = Array.isArray(items) && items.length ? items[items.length - 1] : null;
    const oldestId = extractMessageId(oldestItem);
    if (oldestId && oldestId !== normalizedCursor.cursorValue) {
        if (hasMoreFlag === true) return oldestId;
        if (Array.isArray(items) && items.length >= pageSize) return oldestId;
    }

    return null;
}

function extractDialogId(raw) {
    const source = unwrapPayload(raw);
    const id =
        source?.chatId ??
        source?.dialogId ??
        source?.id ??
        source?.chat?.id ??
        source?.dialog?.id ??
        null;
    if (id == null || id === "") return "";
    return String(id);
}

function normalizeActionCategory(value) {
    const raw = String(value || "").trim().toUpperCase();
    if (raw === "BUG" || raw === "ERROR" || raw === "ISSUE") return "BUG";
    return "SUPPORT";
}

function hasAnyToken(value, tokens = []) {
    const hay = String(value || "").trim().toLowerCase();
    if (!hay) return false;
    return tokens.some((token) => hay.includes(String(token).trim().toLowerCase()));
}

function canIgnoreActionLookupError(err) {
    const status = Number(err?.status ?? err?.response?.status ?? NaN);
    const code = String(err?.code || err?.error || "").trim().toUpperCase();
    if (code === "CHAT_ID_INVALID") return true;
    return [400, 404, 405, 422, 501].includes(status);
}

function pickActionDialogFromList(dialogs = [], category = "SUPPORT") {
    const safe = Array.isArray(dialogs) ? dialogs : [];
    if (!safe.length) return null;

    const categoryToken = normalizeActionCategory(category);
    const categoryHints =
        categoryToken === "BUG"
            ? ["bug", "error", "issue", "ошиб", "баг"]
            : ["support", "help", "поддерж", "саппорт"];

    const genericHints = ["action", "action-feed", "action_feed", "moderation", "admin", "систем", "модерац", "support"];
    const adminRoleHints = ["ADMIN", "MODERATOR", "SUPPORT"];
    const roleHints = ["admin", "moderator", "support", "админ", "модератор", "поддерж"];

    let fallback = null;
    let best = null;
    let bestScore = -1;
    for (const rawDialog of safe) {
        const source = rawDialog && typeof rawDialog === "object" ? rawDialog : {};
        const id = extractDialogId(source);
        if (!id) continue;

        const code = String(
            source.code ??
                source.dialogCode ??
                source.dialog_code ??
                source.type ??
                source.chatType ??
                source.chat_type ??
                source.dialogType ??
                source.dialog_type ??
                source.kind ??
                ""
        ).trim();
        const typeRaw = String(
            source.type ??
                source.chatType ??
                source.chat_type ??
                source.dialogType ??
                source.dialog_type ??
                source.kind ??
                ""
        ).trim();
        const title = String(source.title ?? source.name ?? source.dialogTitle ?? source.displayName ?? "").trim();
        const topic = String(
            source.topic ??
                source.intent ??
                source.purpose ??
                source.description ??
                source.dialogPurpose ??
                source.dialog_purpose ??
                source.alias ??
                source.slug ??
                source.key ??
                source.chatKey ??
                source.chat_key ??
                source.systemName ??
                source.system_name ??
                ""
        ).trim();
        const partnerRole = String(
            source.partnerRole ??
                source.partner_role ??
                source.otherUserRole ??
                source.other_user_role ??
                source.peerRole ??
                source.peer_role ??
                source.userRole ??
                source.user_role ??
                ""
        ).trim();
        const members = Array.isArray(source.members) ? source.members : [];
        const memberRolesBlob = members
            .map((member) =>
                String(
                    member?.role ??
                        member?.memberRole ??
                        member?.chatRole ??
                        member?.userRole ??
                        member?.roleCode ??
                        member?.role_code ??
                        ""
                ).trim()
            )
            .filter(Boolean)
            .join(" ");
        const labelBlob = [code, title, topic, typeRaw, partnerRole, memberRolesBlob].join(" ").trim();

        const explicitAction =
            source.isActionFeed === true ||
            source.actionFeed === true ||
            source.isSystem === true ||
            hasAnyToken(code, ["action", "action-feed", "support", "admin", "moderation"]);
        const looksLikeAdminPeer =
            hasAnyToken(partnerRole, roleHints) ||
            adminRoleHints.includes(partnerRole.toUpperCase()) ||
            hasAnyToken(memberRolesBlob, roleHints);

        let score = 0;
        if (explicitAction) score += 6;
        if (hasAnyToken(labelBlob, genericHints)) score += 4;
        if (hasAnyToken(labelBlob, categoryHints)) score += 4;
        if (looksLikeAdminPeer) score += 3;
        if (hasAnyToken(typeRaw, ["system", "service", "support", "action"])) score += 2;

        if (explicitAction && hasAnyToken(labelBlob, categoryHints)) {
            return { ...source, id };
        }
        if (explicitAction) fallback = { ...source, id };

        if (hasAnyToken(labelBlob, genericHints) && hasAnyToken(labelBlob, categoryHints)) {
            return { ...source, id };
        }
        if (!fallback && hasAnyToken(labelBlob, genericHints)) {
            fallback = { ...source, id };
        }

        if (score > bestScore) {
            bestScore = score;
            best = { ...source, id };
        }
    }
    if (fallback) return fallback;
    if (best && bestScore >= 6) return best;
    return null;
}

async function openDialog(payload = {}) {
    const raw = await request("/api/chat/open", {
        method: "POST",
        json: payload,
    });
    const chatId = extractDialogId(raw);
    return {
        raw,
        item: unwrapPayload(raw),
        chatId,
        dialogId: chatId,
    };
}

export const chatApi = {
    listDialogs: async () => {
        const res = await request("/api/chat/dialogs");
        return pickArray(res);
    },

    listMessages: async (dialogId, { limit = 30, cursor } = {}) => {
        const pageSize = parsePositiveInt(limit, 30);
        const normalizedCursor = normalizeMessagesCursor(cursor);
        const qs = new URLSearchParams();
        qs.set("limit", String(pageSize));
        qs.set("size", String(pageSize));
        qs.set("pageSize", String(pageSize));
        if (normalizedCursor.cursorValue) {
            qs.set("cursor", normalizedCursor.cursorValue);
            qs.set("before", normalizedCursor.cursorValue);
            qs.set("beforeMessageId", normalizedCursor.cursorValue);
        } else if (normalizedCursor.page != null) {
            qs.set("page", String(normalizedCursor.page));
        }

        const res = await request(`/api/chat/dialogs/${String(dialogId)}/messages?${qs.toString()}`);
        const items = pickArray(res);
        const nextCursor = pickNextMessagesCursor(res, items, pageSize, normalizedCursor);
        return { ...(res && typeof res === "object" && !Array.isArray(res) ? res : {}), items, nextCursor };
    },

    sendMessage: async (dialogId, { text, content, clientId, files, replyToKey, replyToMessageId, replyToClientId } = {}) => {
        const bodyText = content != null ? content : text;
        const did = String(dialogId);
        const modernMessagesPath = `/api/chat/${did}/messages`;
        const modernSendFilesPath = `/api/chat/${did}/send-files`;
        const legacyMessagesPath = `/api/chat/dialogs/${did}/messages`;

        const hasFiles = Array.isArray(files) && files.length > 0;
        if (hasFiles) {
            const fd = new FormData();
            if (bodyText != null) fd.append("content", bodyText);
            if (clientId) fd.append("clientId", clientId);

            if (replyToMessageId != null) fd.append("replyToMessageId", String(replyToMessageId));
            if (replyToClientId != null) fd.append("replyToClientId", String(replyToClientId));
            else if (replyToKey != null) fd.append("replyToKey", String(replyToKey));

            for (const f of files) fd.append("files", f);

            return requestWithEndpointFallback(
                modernSendFilesPath,
                legacyMessagesPath,
                { method: "POST", body: fd, headers: {} }
            );
        }

        return requestWithEndpointFallback(modernMessagesPath, legacyMessagesPath, {
            method: "POST",
            json: {
                content: bodyText || "",
                clientId,
                ...(replyToMessageId != null
                    ? { replyToMessageId }
                    : replyToClientId != null
                        ? { replyToClientId: String(replyToClientId) }
                        : replyToKey != null
                            ? { replyToKey: String(replyToKey) }
                            : {}),
            },
        });
    },

    openPsychologistDialog: async (psychologistUserId) => {
        const uid = Number(psychologistUserId);
        const payload = Number.isFinite(uid) && uid > 0 ? { psychologistUserId: uid } : {};
        return openDialog(payload);
    },

    openClientDialog: async (clientId) => {
        const cid = Number(clientId);
        const payload = Number.isFinite(cid) && cid > 0 ? { clientId: cid } : {};
        return openDialog(payload);
    },

    openActionDialog: async ({ category = "SUPPORT" } = {}) => {
        const normalizedCategory = normalizeActionCategory(category);

        let dialogs = [];
        try {
            dialogs = await chatApi.listDialogs();
        } catch {
            dialogs = [];
        }

        const existing = pickActionDialogFromList(dialogs, normalizedCategory);
        if (existing?.id) {
            return { raw: existing, item: existing, chatId: String(existing.id), dialogId: String(existing.id) };
        }

        const lookupPaths = [
            `/api/chat/support/dialog?category=${encodeURIComponent(normalizedCategory)}`,
            `/api/chat/action/dialog?category=${encodeURIComponent(normalizedCategory)}`,
            `/api/chat/action-feed/dialog?category=${encodeURIComponent(normalizedCategory)}`,
        ];
        for (const path of lookupPaths) {
            try {
                const byLookup = await request(path, { method: "GET" });
                const lookupId = extractDialogId(byLookup);
                if (lookupId) {
                    const item = unwrapPayload(byLookup);
                    return { raw: byLookup, item, chatId: String(lookupId), dialogId: String(lookupId) };
                }
            } catch (err) {
                if (!canIgnoreActionLookupError(err)) throw err;
            }
        }

        const openPayloadCandidates = [
            { category: normalizedCategory },
            { purpose: normalizedCategory },
            { intent: normalizedCategory },
            { type: normalizedCategory },
            { chatType: normalizedCategory },
            { actionFeed: true, category: normalizedCategory },
        ];
        for (const payload of openPayloadCandidates) {
            try {
                const opened = await openDialog(payload);
                const openedId = String(opened?.chatId || opened?.dialogId || "");
                if (!openedId) continue;

                let probe = opened?.item || opened?.raw || null;
                try {
                    const full = await chatApi.getDialog(openedId);
                    if (full && typeof full === "object") probe = full;
                } catch {
                    // ignore, fallback to open response
                }

                const matched = pickActionDialogFromList([probe], normalizedCategory);
                if (matched?.id || matched?.chatId || matched?.dialogId) {
                    const resolvedId = extractDialogId(matched) || openedId;
                    return { raw: matched, item: matched, chatId: String(resolvedId), dialogId: String(resolvedId) };
                }
            } catch (err) {
                if (!canIgnoreActionLookupError(err)) throw err;
            }
        }

        throw new Error("Чат поддержки не найден. Нужен backend endpoint для единого action-чата.");
    },

    sendToActionDialog: async ({ text, content, category = "SUPPORT", pagePath = "" } = {}) => {
        const body = String(content ?? text ?? "").trim();
        if (!body) throw new Error("Пустое сообщение");
        const normalizedCategory = normalizeActionCategory(category);
        const route = String(pagePath || "").trim();
        const raw = await request("/api/chat/action-feed/notify", {
            method: "POST",
            json: { category: normalizedCategory, content: body, pagePath: route || undefined },
        });
        const payload = unwrapPayload(raw);
        const dialogId = extractDialogId(payload) || extractDialogId(raw);
        return { dialogId, message: payload, dialog: payload, raw };
    },

    markMessagesRead: async (dialogId, messageIds = []) => {
        const did = String(dialogId);
        const ids = Array.isArray(messageIds) ? messageIds.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0) : [];
        if (!ids.length) return { updated: 0 };

        const upToMessageId = ids[ids.length - 1];
        const json = {
            messageIds: ids,
            message_ids: ids,
            readMessageIds: ids,
            read_message_ids: ids,
            upToMessageId,
            up_to_message_id: upToMessageId,
        };

        return requestFirstAvailable(
            [
                `/api/chat/dialogs/${did}/messages/read`,
                `/api/chat/dialogs/${did}/read`,
                `/api/chat/${did}/read`,
                `/api/chat/${did}/messages/read`,
                `/api/chat/messages/read`,
            ],
            { method: "POST", json: { dialogId: did, chatId: did, ...json } }
        );
    },

    markDialogRead: async (dialogId) => {
        const did = String(dialogId);
        if (!did) return { updated: 0 };

        return requestFirstAvailable(
            [
                `/api/chat/dialogs/${did}/read`,
                `/api/chat/${did}/read`,
                `/api/chat/dialogs/${did}/messages/read`,
            ],
            { method: "POST", json: { dialogId: did, chatId: did } }
        );
    },

    getMessageReaders: async (messageId) => {
        return request(`/api/chat/messages/${String(messageId)}/readers`);
    },

    reactToMessage: async (messageId, { emoji, reactionKind, add, clientId } = {}) => {
        return request(`/api/chat/messages/${String(messageId)}/reactions`, {
            method: "POST",
            json: buildReactionTogglePayload({
                reaction: emoji ?? reactionKind,
                add,
                clientId,
                targetType: REACTION_TARGET_TYPES.CHAT_MESSAGE,
                targetId: messageId,
            }),
        });
    },

    getReactionUsers: async (messageId, emoji) => {
        const qs = new URLSearchParams();
        if (emoji != null) qs.set("emoji", String(emoji));
        const res = await request(`/api/chat/messages/${String(messageId)}/reactions/users?${qs.toString()}`);
        return pickArray(res);
    },

    updateDialogNotifications: async (dialogId, payload = {}) => {
        const did = String(dialogId);
        return requestWithEndpointFallback(
            `/api/chat/dialogs/${did}/notification-settings`,
            `/api/chat/dialogs/${did}/notifications`,
            { method: "PATCH", json: payload }
        );
    },

    editMessage: async (messageId, { text, files, keepAttachmentIds, removeAttachmentIds } = {}) => {
        const mid = String(messageId);
        const uploadFiles = Array.isArray(files) ? files : [];
        const keepIds = Array.isArray(keepAttachmentIds) ? keepAttachmentIds.map(String) : [];
        const removeIds = Array.isArray(removeAttachmentIds) ? removeAttachmentIds.map(String) : [];
        const hasMediaEdit = uploadFiles.length > 0 || keepIds.length > 0 || removeIds.length > 0;

        if (hasMediaEdit) {
            const fd = new FormData();
            fd.append("content", text != null ? String(text) : "");
            fd.append("keepAttachmentIds", JSON.stringify(keepIds));
            fd.append("removeAttachmentIds", JSON.stringify(removeIds));
            uploadFiles.forEach((file) => fd.append("files", file));

            return requestWithEndpointFallback(
                `/api/chat/messages/${mid}/edit`,
                `/api/chat/messages/${mid}`,
                { method: "POST", body: fd, headers: {} }
            );
        }

        return requestWithEndpointFallback(
            `/api/chat/messages/${mid}/edit`,
            `/api/chat/messages/${mid}`,
            {
                method: "POST",
                json: {
                    content: text != null ? String(text) : "",
                    keepAttachmentIds: keepIds,
                    removeAttachmentIds: removeIds,
                },
            }
        );
    },

    deleteMessage: async (messageId) => {
        return request(`/api/chat/messages/${String(messageId)}/delete`, {
            method: "POST",
            json: {},
        });
    },

    getDialog: async (dialogId) => {
        const did = String(dialogId);
        return request(`/api/chat/dialogs/${did}`);
    },

    listMembers: async (dialogId) => {
        const did = String(dialogId);
        const res = await request(`/api/chat/dialogs/${did}/members`);
        if (Array.isArray(res)) return res;
        return res?.items || res?.content || res?.data || [];
    },

    // ✅ поиск пользователей (подстрой endpoint под свой бэк)
    searchUsers: async (q) => {
        const qs = new URLSearchParams();
        qs.set("q", String(q || ""));
        const res = await request(`/api/users/search?${qs.toString()}`);
        return pickArray(res);
    },

    // ✅ add member (GROUP)
    addMember: async (chatId, userId) => {
        return request(`/api/chat/dialogs/${String(chatId)}/members`, {
            method: "POST",
            body: JSON.stringify({ userId }),
            headers: { "Content-Type": "application/json" },
        });
    },

    // ✅ remove member (GROUP)
    removeMember: async (chatId, userId) => {
        return request(`/api/chat/dialogs/${String(chatId)}/members/${String(userId)}`, {
            method: "DELETE",
        });
    },

    searchMessages: async (dialogId, { q = "", limit = 30, cursor } = {}) => {
        const qs = new URLSearchParams();
        qs.set("q", String(q || ""));
        qs.set("limit", String(limit || 30));
        if (cursor) qs.set("cursor", String(cursor));
        const res = await request(`/api/chat/dialogs/${String(dialogId)}/messages/search?${qs.toString()}`);
        if (res && typeof res === "object" && !Array.isArray(res) && Array.isArray(res.items)) return res;
        return { items: pickArray(res), nextCursor: null };
    },

    listSharedFiles: async (dialogId, { limit = 50, cursor } = {}) => {
        const qs = new URLSearchParams();
        qs.set("limit", String(limit || 50));
        if (cursor) qs.set("cursor", String(cursor));
        const res = await request(`/api/chat/dialogs/${String(dialogId)}/attachments?${qs.toString()}`);
        if (res && typeof res === "object" && !Array.isArray(res) && Array.isArray(res.items)) return res;
        return { items: pickArray(res), nextCursor: null };
    },

    renameDialog: async (dialogId, title) => {
        const did = String(dialogId);
        return requestWithEndpointFallback(
            `/api/chat/dialogs/${did}/rename`,
            `/api/chat/dialogs/${did}`,
            {
                method: "PATCH",
                json: { title: String(title || "").trim(), name: String(title || "").trim() },
            }
        );
    },

    updateDialogAvatar: async (dialogId, file) => {
        const did = String(dialogId);
        const fd = new FormData();
        fd.append("avatar", file);
        fd.append("file", file);
        return requestWithEndpointFallback(
            `/api/chat/dialogs/${did}/avatar`,
            `/api/chat/dialogs/${did}`,
            { method: "PATCH", body: fd, headers: {} }
        );
    },

    updateDialogSettings: async (dialogId, payload = {}) => {
        const did = String(dialogId);
        return requestWithEndpointFallback(
            `/api/chat/dialogs/${did}/settings`,
            `/api/chat/dialogs/${did}/permissions`,
            {
                method: "PATCH",
                json: {
                    title: payload.title,
                    writeMode: payload.writeMode,
                    mediaMode: payload.mediaMode,
                    allowedUserIds: Array.isArray(payload.allowedUserIds) ? payload.allowedUserIds : [],
                },
            }
        );
    },

    pinMessage: async (dialogId, messageId, { scope = "self" } = {}) => {
        const did = String(dialogId);
        const mid = String(messageId);
        return requestWithEndpointFallback(
            `/api/chat/dialogs/${did}/pinned-messages`,
            `/api/chat/messages/${mid}/pin`,
            {
                method: "POST",
                json: { messageId: mid, scope: String(scope || "self") },
            }
        );
    },

    unpinMessage: async (dialogId, messageId, { scope = "self" } = {}) => {
        const did = String(dialogId);
        const mid = String(messageId);
        return requestWithEndpointFallback(
            `/api/chat/dialogs/${did}/pinned-messages/${mid}?scope=${encodeURIComponent(String(scope || "self"))}`,
            `/api/chat/messages/${mid}/unpin`,
            {
                method: "DELETE",
                json: { scope: String(scope || "self") },
            }
        );
    },

    getDialogInfo: async (chatId, { includeMembers = true } = {}) => {
        const qs = new URLSearchParams();
        qs.set("includeMembers", includeMembers ? "true" : "false");
        const res = await request(`/api/chat/dialogs/${String(chatId)}/info?${qs.toString()}`);
        return res;
    },
};
